import type { FetchQueryOptions } from "@tanstack/react-query";
import type { FeaturePreferences } from "models/types/agency-settings";
import {
  Await,
  Navigate,
  useLoaderData,
  type LoaderFunction,
  type LoaderFunctionArgs,
  type NavigateProps,
} from "react-router-dom";
import type { Route } from "lib/react-router-dom";
import { cacheFirstFetch } from "utils/cache-first-fetch";
import { isPromise } from "utils/is-promise";

type LockedRouteProps = {
  children: React.ReactNode;
} & Partial<NavigateProps>;

function LockedRoute({
  children,
  to = "..",
  ...navigateProps
}: LockedRouteProps) {
  const loaderData = useLoaderData() as { isLocked?: boolean } | undefined;

  return (
    <Await resolve={loaderData?.isLocked}>
      {(isLocked) => {
        if (isLocked) {
          return <Navigate to={to} replace {...navigateProps} />;
        }

        return children;
      }}
    </Await>
  );
}

type CreateIsLockedLoaderArgs<T> = {
  query:
    | FetchQueryOptions<T>
    | ((args: LoaderFunctionArgs) => FetchQueryOptions<T>);
  getFeaturePreferences?: (
    data: T,
    args: LoaderFunctionArgs,
  ) => FeaturePreferences | undefined | Promise<FeaturePreferences | undefined>;
  isRouteLocked: (
    data: T,
    featurePreferences: FeaturePreferences | undefined,
  ) => boolean | Promise<boolean>;
};

const createIsLockedLoader = <T,>({
  query,
  isRouteLocked,
  getFeaturePreferences,
}: CreateIsLockedLoaderArgs<T>) => {
  const isLockedLoader: LoaderFunction = (args) => {
    const queryOptions = typeof query === "function" ? query(args) : query;

    let isLocked: boolean | Promise<boolean>;

    const data = cacheFirstFetch<T>(queryOptions);

    if (isPromise(data)) {
      isLocked = data.then(async (resolvedData) => {
        const prefs = getFeaturePreferences
          ? await getFeaturePreferences(resolvedData, args)
          : undefined;

        return isRouteLocked(resolvedData, prefs);
      });
    } else {
      const prefs = getFeaturePreferences
        ? getFeaturePreferences(data, args)
        : undefined;

      if (isPromise(prefs)) {
        isLocked = prefs.then((resolvedPrefs) =>
          isRouteLocked(data, resolvedPrefs),
        );
      } else {
        isLocked = isRouteLocked(data, prefs);
      }
    }

    return { isLocked };
  };

  return isLockedLoader;
};

export const createLockedRoute = <T,>(
  routeObject: Omit<Route, "loader"> &
    CreateIsLockedLoaderArgs<T> & {
      redirectProps?: Omit<LockedRouteProps, "children">;
    },
): Route => {
  const {
    query,
    isRouteLocked,
    Component,
    element,
    redirectProps = {},
    getFeaturePreferences,
    ...rest
  } = routeObject;
  const isLockedLoader = createIsLockedLoader({
    query,
    isRouteLocked,
    getFeaturePreferences,
  });

  const routeElement = Component ? (
    <LockedRoute {...redirectProps}>
      <Component />
    </LockedRoute>
  ) : (
    <LockedRoute {...redirectProps}>{element}</LockedRoute>
  );

  return {
    loader: isLockedLoader,
    element: routeElement,
    ...(rest as Route),
  };
};
